/* eslint-disable react-hooks/exhaustive-deps */
import type { Socket } from "socket.io-client"

import { useCallback, useEffect, useRef } from "react"
import type { RefObject } from "react"

import eq from "lodash-es/eq"
import includes from "lodash-es/includes"
import isNumber from "lodash-es/isNumber"
import { useIsomorphicLayoutEffect, useUnmount } from "react-use"

import { addCoachingChatSocketBreadcrumb, captureCoachingChatSocketErrorWithSentry } from "@/3514/utils"
import { minutes } from "@/3514/utils/time.utils"
import { type TEmptyCallback, emptyCallback } from "@/shared/types/functions"

import {
    type TCoachingChatCreateSocketTokenMutation as TSocketTokenMutation,
    type TCoachingChatCreateSocketTokenMutationResponse as TSocketTokenMutationResponse,
    acquireCoachingChatSocketLease,
    coachingChatSocket as socket,
    useCoachingChatSocketTokenMutation as useSocketTokenMutation
} from "../api"
import {
    setCoachingChatConversationApiMode as setConversationApiMode,
    useCoachingChatConversationDispatcher as useConversationDispatcher,
    useCoachingChatConversationStatusSelector as useConversationStatusSelector
} from "../store"
import {
    ECoachingChatConversationApiMode as EConversationApiMode,
    ECoachingChatConversationStatus as EConversationStatus,
    ECoachingChatEntityMode as EEntityMode,
    type _TCoachingChatHookSharedParams as _TSharedParams
} from "../types"
import { coachingChatApiUtils as apiUtils } from "../utils"
import { logCoachingChatState as logChatState } from "../utils/coaching-chat.diagnostics"

const {
    socket: { setToken, isTokenValid }
} = apiUtils

const REST_FALLBACK_FAILED_ATTEMPTS: number = 3

type _TParams = _TSharedParams & {
    isChatEnabled: boolean
    existingConversationId: number
}

function useCoachingChatConnectionManager({
    entityId,
    entityMode,
    wasChatCompleted,
    isChatEnabled,
    existingConversationId
}: _TParams): void {
    const isFullMode: boolean = eq(entityMode, EEntityMode.Full)
    const isSocketEligible: boolean =
        isChatEnabled && !wasChatCompleted && isFullMode && Boolean(JSAPP_CONF?.websockets_enabled)

    const conversationDispatcher: ReturnType<typeof useConversationDispatcher> = useConversationDispatcher(entityId)

    const conversationStatus: EConversationStatus = useConversationStatusSelector(entityId)

    const { mutateAsync, reset: resetMutation }: TSocketTokenMutation = useSocketTokenMutation({
        onSuccess: async (resp: TSocketTokenMutationResponse): Promise<void> => {
            setToken(resp.data?.token)

            // Stage the fresh token for the next handshake in all cases.
            socket.auth = { token: resp.data.token }

            // The server validates the WS token only at connect, so a live connection
            // stays valid even after the token expires. If we're still connected, just
            // keep the fresh token ready for the next organic reconnect — do NOT force a
            // close()/connect() (that caused a periodic ~270ms flicker + REST flip every
            // token cycle).
            if (socket.connected) {
                // eslint-disable-next-line no-console
                console.info("[cm-ws] token re-minted (seamless, staying connected)")
                addCoachingChatSocketBreadcrumb("[cm-ws] token re-minted (seamless)")

                return
            }

            // Disconnected: (re)establish the connection with the fresh token.
            // eslint-disable-next-line no-console
            console.info("[cm-ws] token re-minted -> reconnecting")
            addCoachingChatSocketBreadcrumb("[cm-ws] token re-minted -> reconnecting")

            socket.close()

            socket.connect()
        },
        onError: async (): Promise<void> => {
            setToken(String(""))

            // A live socket remains authenticated. Keep it while the token endpoint
            // is temporarily unavailable and let the periodic check retry the mint.
            if (socket.connected) {
                // eslint-disable-next-line no-console
                console.error("[cm-ws] token mint failed -> keeping the active socket")
                addCoachingChatSocketBreadcrumb("[cm-ws] token mint failed -> active socket kept", void 0, "error")

                return
            }

            // eslint-disable-next-line no-console
            console.error("[cm-ws] token mint failed -> falling back to REST")
            addCoachingChatSocketBreadcrumb("[cm-ws] token mint failed -> REST", void 0, "error")

            socket.disconnect()

            socket.close()

            setConversationApiMode(EConversationApiMode.Rest)
        }
    })

    useIsomorphicLayoutEffect((): TEmptyCallback => {
        // A socket opening while `wasChatCompleted` is true would be wrong; a socket opening for a
        // chat the participant has already finished means this flag arrived stale.
        logChatState("socket-eligibility", {
            entityId,
            isSocketEligible,
            isChatEnabled,
            wasChatCompleted,
            isFullMode,
            websocketsEnabled: Boolean(JSAPP_CONF?.websockets_enabled),
            existingConversationId
        })

        if (!isSocketEligible) return emptyCallback

        const releaseSocketLease: TEmptyCallback = acquireCoachingChatSocketLease()

        if (isTokenValid()) socket.connect()
        else void mutateAsync(void 0).catch((): void => void 0)

        return releaseSocketLease
    }, [isSocketEligible])

    // Read through a ref inside socket callbacks: the effect below subscribes once (deps are
    // [isSocketEligible] only), so a closure over `conversationStatus` would go stale.
    const conversationStatusRef: RefObject<EConversationStatus> = useRef<EConversationStatus>(conversationStatus)
    conversationStatusRef.current = conversationStatus

    const hasEverConnectedRef: RefObject<boolean> = useRef<boolean>(socket.connected)

    const failedReconnectsRef: RefObject<number> = useRef<number>(0)

    const hasAttemptedTokenRefreshRef: RefObject<boolean> = useRef<boolean>(false)

    useEffect((): TEmptyCallback => {
        if (!isSocketEligible) return emptyCallback

        function connector(): void {
            hasEverConnectedRef.current = true
            failedReconnectsRef.current = 0
            hasAttemptedTokenRefreshRef.current = false

            setConversationApiMode(EConversationApiMode.Sockets)

            // Never demote a finished chat back to Connected. The eligibility flag above is
            // computed from the chapter payload, which lags reality (the backend stamps
            // completion after streaming the summary), so this socket can legitimately open
            // for a conversation the store already knows is over.
            if (
                isNumber(existingConversationId) &&
                !includes<EConversationStatus>(
                    [EConversationStatus.GeneratingSummary, EConversationStatus.Completed],
                    conversationStatusRef.current
                )
            )
                conversationDispatcher.changeStatus(EConversationStatus.Connected)
        }

        function fallbackToRest(): void {
            setConversationApiMode(EConversationApiMode.Rest)
        }

        function refreshToken(): void {
            hasAttemptedTokenRefreshRef.current = true
            void mutateAsync(void 0).catch(fallbackToRest)
        }

        function disconnector(reason: Socket.DisconnectReason): void {
            // Transport failures are reconnectable and must not replace the rendered
            // conversation with the REST loading state. Socket.IO reconnects those
            // automatically; only a server-forced disconnect needs an explicit connect.
            if (reason !== "io server disconnect") return

            if (!isTokenValid()) {
                if (hasAttemptedTokenRefreshRef.current) fallbackToRest()
                else refreshToken()

                return
            }

            socket.connect()
        }

        function errorConnector(err: Error): void {
            captureCoachingChatSocketErrorWithSentry(err)

            // A stale/expired WS token makes every (re)connect fail the handshake,
            // even after we've connected before. Re-mint it once per disconnect
            // episode and let socket.io retry with the fresh token
            // (hasAttemptedTokenRefreshRef is reset on a successful connect).
            if (!isTokenValid() && !hasAttemptedTokenRefreshRef.current) {
                refreshToken()
                return
            }

            if (!hasEverConnectedRef.current && !hasAttemptedTokenRefreshRef.current) {
                refreshToken()
                return
            }

            failedReconnectsRef.current += 1

            if (failedReconnectsRef.current >= REST_FALLBACK_FAILED_ATTEMPTS) fallbackToRest()
        }

        socket.on("connect", connector)
        socket.on("disconnect", disconnector)
        socket.on("connect_error", errorConnector)
        socket.io.on("reconnect_failed", fallbackToRest)

        // If the socket is already connected at (re)mount, the "connect" event won't fire
        // again — sync the connected state now. Otherwise apiMode (reset to null by the
        // rest-queries-manager unmount) would stay null and the chat would be stuck on the
        // loader after a remount over a still-live socket (seamless re-mint / leased teardown).
        if (socket.connected) connector()

        return (): void => {
            socket.off("connect", connector)
            socket.off("disconnect", disconnector)
            socket.off("connect_error", errorConnector)
            socket.io.off("reconnect_failed", fallbackToRest)
        }
    }, [isSocketEligible, existingConversationId])

    const checkConnection: TEmptyCallback = useCallback((): void => {
        if (
            !isChatEnabled ||
            wasChatCompleted ||
            !isFullMode ||
            !JSAPP_CONF?.websockets_enabled ||
            includes<EConversationStatus>(
                [EConversationStatus.GeneratingSummary, EConversationStatus.Completed],
                conversationStatus
            )
        )
            return void 0

        // Re-mint whenever the token is stale — even if the socket is currently
        // disconnected — so an expired token doesn't leave the chat stuck after a drop.
        if (isTokenValid()) return void 0

        void mutateAsync(void 0).catch((): void => void 0)

        return void 0
    }, [isChatEnabled, wasChatCompleted, isFullMode, conversationStatus, mutateAsync])

    useIsomorphicLayoutEffect((): TEmptyCallback => {
        const int: ReturnType<typeof setInterval> = setInterval((): void => void checkConnection(), minutes(2))

        return (): void => void clearTimeout(int)
    }, [checkConnection])

    useEffect((): void => {
        if (!isSocketEligible) setConversationApiMode(EConversationApiMode.Rest)
    }, [isSocketEligible])

    useUnmount((): void => {
        if (isChatEnabled) resetMutation()
    })
}

export { useCoachingChatConnectionManager }
